import React from 'react'
import styled from 'styled-components'
import { BackButtonContainer, TextButton } from './styled'
import { color, position, space, layout, LayoutProps, SpaceProps, ColorProps, PositionProps } from 'styled-system'
import { Link } from 'react-router-dom'
import ArrowLeftIcon from 'assets/icons/ArrowLeftIcon'

export type BlockProps = LayoutProps & SpaceProps & ColorProps & PositionProps

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

interface IButtonStyleProps {
  background?: string
  color?: string
  width?: string
  height?: string
  direction?: string
  justify?: string
  align?: string
  bgColor?: string
  padding?: string
  margin?: string
  border?: string
  borderRadius?: string
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  disabled?: boolean
}

const sizeStyles: Record<ButtonSize, string> = {
  sm: `
    min-height: 40px;
    min-width: 96px;
    padding: 8px 16px;
    font-size: 14px;
  `,
  md: `
    min-height: 48px;
    min-width: 112px;
    padding: 10px 22px;
    font-size: 15px;
  `,
  lg: `
    min-height: 56px;
    min-width: 128px;
    padding: 14px 28px;
    font-size: 16px;
  `,
}

const resolveVariantStyles = (props: IButtonStyleProps) => {
  const { variant = 'primary', disabled, background, color: buttonTextColor, border } = props

  if (disabled) {
    return `
      background: #eef2f6;
      color: #a3afbb;
      border: 1px solid #d7e0ea;
      box-shadow: none;
      cursor: not-allowed;
      pointer-events: none;
    `
  }

  if (variant === 'secondary') {
    return `
      background: ${background || 'rgba(255, 255, 255, 0.96)'};
      color: ${buttonTextColor || '#2D749C'};
      border: ${border || '1px solid #B6C2D6'};
      box-shadow: 0 10px 24px rgba(45, 116, 156, 0.12);
    `
  }

  if (variant === 'ghost') {
    return `
      background: transparent;
      color: ${buttonTextColor || '#2D749C'};
      border: ${border || '1px solid transparent'};
      box-shadow: none;
    `
  }

  if (variant === 'danger') {
    return `
      background: ${background || '#C93B55'};
      color: ${buttonTextColor || '#FFFFFF'};
      border: ${border || '1px solid transparent'};
      box-shadow: 0 12px 28px rgba(201, 59, 85, 0.22);
    `
  }

  return `
    background: ${background || '#C6016B'};
    color: ${buttonTextColor || '#FFFFFF'};
    border: ${border || '1px solid transparent'};
    box-shadow: 0 12px 30px rgba(36, 93, 125, 0.18);
  `
}

const hoverStyles: Record<ButtonVariant, string> = {
  primary: `
    transform: translateY(-1px);
    box-shadow: 0 16px 34px rgba(36, 93, 125, 0.22);
    filter: saturate(1.05);
  `,
  secondary: `
    transform: translateY(-1px);
    background: #2D749C;
    color: #FFFFFF;
    border-color: #2D749C;
    box-shadow: 0 14px 28px rgba(45, 116, 156, 0.18);
  `,
  ghost: `
    background: rgba(45, 116, 156, 0.08);
    color: #245D7D;
  `,
  danger: `
    transform: translateY(-1px);
    box-shadow: 0 16px 32px rgba(201, 59, 85, 0.26);
    filter: saturate(1.03);
  `,
}

const activeStyles: Record<ButtonVariant, string> = {
  primary: `
    transform: translateY(0);
    box-shadow: 0 8px 18px rgba(36, 93, 125, 0.18);
  `,
  secondary: `
    transform: translateY(0);
    box-shadow: 0 8px 18px rgba(45, 116, 156, 0.14);
  `,
  ghost: `
    transform: translateY(0);
    background: rgba(45, 116, 156, 0.12);
  `,
  danger: `
    transform: translateY(0);
    box-shadow: 0 8px 18px rgba(201, 59, 85, 0.2);
  `,
}

const BaseButtonStyled = styled.button<IButtonStyleProps>`
  height: ${(props) => props.height || 'auto'};
  width: ${(props) => (props.fullWidth ? '100%' : props.width || 'auto')};
  margin: ${(props) => props.margin || '4px 2px'};
  border-radius: ${(props) => props.borderRadius || (props.variant === 'ghost' ? '12px' : '14px')};
  text-align: center;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: 'Public Sans', sans-serif;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease, background-color 0.18s ease,
    border-color 0.18s ease, color 0.18s ease;
  ${(props) => sizeStyles[props.size || 'md']}
  ${(props) => resolveVariantStyles(props)}

  &&:hover {
    ${(props) => hoverStyles[props.variant || 'primary']}
  }

  &&:active {
    ${(props) => activeStyles[props.variant || 'primary']}
  }

  @media (max-width: 1340px) {
    font-size: 14px;
  }
`

interface IButtonProps {
  className?: string
  type?: 'button' | 'submit' | 'reset'
  onClick?: () => void
  onPointerDown?: React.PointerEventHandler<HTMLButtonElement>
  onDisabledClick?: () => void
  children?: any
  disabled?: boolean
  fakeDisabled?: boolean
  style?: React.CSSProperties
  onContextMenu?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  onContextMenuCapture?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
  color?: string
  background?: string
  border?: string
  borderRadius?: string
  width?: string
  height?: string
  margin?: string
  ariaLabel?: string
  ariaPressed?: boolean
}

export const Button = (props: IButtonProps) => {
  const {
    onClick,
    onPointerDown,
    onDisabledClick,
    children,
    type,
    disabled,
    fakeDisabled,
    style,
    onContextMenu,
    onContextMenuCapture,
    className,
    variant = 'primary',
    size = 'md',
    fullWidth,
    leadingIcon,
    trailingIcon,
    color: buttonColor,
    background,
    border,
    borderRadius,
    width,
    height,
    margin,
    ariaLabel,
    ariaPressed,
  } = props

  return (
    <BaseButtonStyled
      className={className}
      onContextMenu={onContextMenu}
      onContextMenuCapture={onContextMenuCapture}
      onPointerDown={onPointerDown}
      style={style}
      background={background}
      color={buttonColor}
      border={border}
      borderRadius={borderRadius}
      width={width}
      height={height}
      margin={margin}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled && !fakeDisabled}
      type={type}
      onClick={() => {
        if (!disabled && onClick) onClick()
        if (disabled && onDisabledClick) onDisabledClick()
      }}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </BaseButtonStyled>
  )
}

export const LinkButton = styled(Link)<BlockProps>`
  color: ${(props) => props.theme.colors.main};
  padding: 1rem;
  cursor: pointer;
  border-radius: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  ${layout}
  ${space}
  ${color}
  ${position}
`

export const ButtonHighlight = styled.button`
  background: rgba(232, 93, 117, 0.08);
  border: 1px solid #f1b7c2;
  border-radius: 14px;
  color: #c93b55;
  padding: 12px 20px;
  text-align: center;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-family: 'Public Sans', sans-serif;
  font-weight: 700;
  margin: 4px 2px;
  cursor: pointer;
  transition: transform 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    background: #c93b55;
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 14px 28px rgba(201, 59, 85, 0.18);
  }
`

export const StrokedButton = (props: IButtonProps) => {
  return <Button {...props} variant={props.variant || 'secondary'} borderRadius={props.borderRadius || '999px'} />
}

export const PrimaryButton = (props: IButtonProps) => {
  return <Button {...props} variant="primary" />
}

export const SecondaryButton = (props: IButtonProps) => {
  return <Button {...props} variant="secondary" />
}

export const GhostButton = (props: IButtonProps) => {
  return <Button {...props} variant="ghost" />
}

export const DangerButton = (props: IButtonProps) => {
  return <Button {...props} variant="danger" />
}

interface IBackButtonProps {
  onClick?: () => void
  style?: React.CSSProperties
  text?: string
  hideIcon?: boolean
  leadingIcon?: React.ReactNode
}

export const BackButton = ({ onClick, style, text, hideIcon, leadingIcon }: IBackButtonProps) => {
  return (
    <BackButtonContainer onClick={onClick} style={style}>
      {!hideIcon && (leadingIcon || <ArrowLeftIcon />)}
      <TextButton>{text ?? 'Atrás'}</TextButton>
    </BackButtonContainer>
  )
}
